import { SegmentedControl } from '@mlm/ui';
import type { RpSectionStyle } from '@mlm/shared';
import { RpFieldRow, RpImageKeyField } from './fields';

/**
 * The presentation knobs for one section.
 *
 * Every control is a closed set, not a free value: that is what keeps a stored
 * token clampable on read, keeps the class strings static (Tailwind purges
 * anything interpolated), and — the practical point — means a non-designer
 * cannot produce an ugly page by typing an arbitrary number. The pre-builder
 * editor exposed exactly one of these (`align`); the other seven are the
 * difference between "a stack of centered text" and a designed page.
 */
export function RpStylePanel({
  style,
  onChange,
}: {
  style: RpSectionStyle;
  onChange: (next: RpSectionStyle) => void;
}) {
  const set = <K extends keyof RpSectionStyle>(key: K, value: RpSectionStyle[K]) =>
    onChange({ ...style, [key]: value });

  return (
    <div className="space-y-3">
      <RpFieldRow
        label="Width"
        hint="Full makes the band reach both edges of the screen — use it for heroes and colour bands."
      >
        <SegmentedControl
          size="sm"
          value={style.width ?? 'normal'}
          onChange={(v) => set('width', v)}
          options={[
            { value: 'narrow', label: 'Narrow' },
            { value: 'normal', label: 'Normal' },
            { value: 'wide', label: 'Wide' },
            { value: 'full', label: 'Full' },
          ]}
          ariaLabel="Width"
        />
      </RpFieldRow>

      <RpFieldRow label="Background">
        <SegmentedControl
          size="sm"
          value={style.bg ?? 'page'}
          onChange={(v) => set('bg', v)}
          options={[
            { value: 'page', label: 'Page' },
            { value: 'muted', label: 'Grey' },
            { value: 'tint', label: 'Tint' },
            { value: 'accent', label: 'Brand' },
            { value: 'dark', label: 'Dark' },
            { value: 'image', label: 'Image' },
          ]}
          ariaLabel="Background"
        />
      </RpFieldRow>

      {style.bg === 'image' ? (
        <>
          <RpFieldRow label="Background image">
            <RpImageKeyField
              value={style.bgImageKey ?? ''}
              onChange={(key) => set('bgImageKey', key)}
            />
          </RpFieldRow>
          <RpFieldRow label="Darken" hint="Keeps text readable over a busy photo.">
            <SegmentedControl
              size="sm"
              value={style.overlay ?? 40}
              onChange={(v) => set('overlay', v)}
              options={[
                { value: 0, label: 'None' },
                { value: 20, label: '20%' },
                { value: 40, label: '40%' },
                { value: 60, label: '60%' },
                { value: 80, label: '80%' },
              ]}
              ariaLabel="Overlay"
            />
          </RpFieldRow>
        </>
      ) : null}

      <RpFieldRow label="Spacing">
        <SegmentedControl
          size="sm"
          value={style.padY ?? 'md'}
          onChange={(v) => set('padY', v)}
          options={[
            { value: 'none', label: 'None' },
            { value: 'xs', label: 'XS' },
            { value: 'sm', label: 'S' },
            { value: 'md', label: 'M' },
            { value: 'lg', label: 'L' },
            { value: 'xl', label: 'XL' },
          ]}
          ariaLabel="Vertical spacing"
        />
      </RpFieldRow>

      <RpFieldRow label="Align">
        <SegmentedControl
          size="sm"
          value={style.align ?? 'left'}
          onChange={(v) => set('align', v)}
          options={[
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Centre' },
            { value: 'right', label: 'Right' },
          ]}
          ariaLabel="Alignment"
        />
      </RpFieldRow>

      <RpFieldRow label="Text colour" hint="Auto follows the background. Override it over a photo.">
        <SegmentedControl
          size="sm"
          value={style.textTone ?? 'auto'}
          onChange={(v) => set('textTone', v)}
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
          ]}
          ariaLabel="Text colour"
        />
      </RpFieldRow>
    </div>
  );
}
